import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
    Leaf, ArrowLeft, Printer, MagnifyingGlass, Funnel,
    Plus, Sparkle, CaretRight, X, GraduationCap,
    PuzzlePiece, ListChecks, Question, Pencil, Palette, ClipboardText
} from '@phosphor-icons/react';

const WORKSHEET_TYPES = {
    word_search: { label: 'Word Search', icon: PuzzlePiece, color: '#22c55e' },
    fill_blanks: { label: 'Fill in the Blanks', icon: Pencil, color: '#38bdf8' },
    quiz_trivia: { label: 'Quiz / Trivia', icon: Question, color: '#f59e0b' },
    matching: { label: 'Matching', icon: ListChecks, color: '#a855f7' },
    colouring_label: { label: 'Colour & Label', icon: Palette, color: '#ec4899' },
    mission_planner: { label: 'Mission Planner', icon: ClipboardText, color: '#ef4444' }
};

const YEAR_LEVELS = ['1-2', '3-4', '5-6', '7-8'];
const TOPICS = ['Australian Animals', 'Australian Sea Creatures', 'Australian Birds'];

// ==================== PRINT RENDERERS ====================

function WordSearchPrint({ content }) {
    // Generate a simple grid from the words
    const words = content.words || [];
    const gridSize = 14;
    const grid = Array.from({ length: gridSize }, () =>
        Array.from({ length: gridSize }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)])
    );
    // Place words in the grid (simplified: horizontal and vertical)
    words.forEach((word, idx) => {
        const row = idx < gridSize ? idx : Math.floor(Math.random() * gridSize);
        const startCol = Math.floor(Math.random() * Math.max(1, gridSize - word.length));
        if (idx % 2 === 0) { // horizontal
            for (let i = 0; i < word.length && startCol + i < gridSize; i++) {
                grid[row][startCol + i] = word[i];
            }
        } else { // vertical
            const col = Math.floor(Math.random() * gridSize);
            const startRow = Math.floor(Math.random() * Math.max(1, gridSize - word.length));
            for (let i = 0; i < word.length && startRow + i < gridSize; i++) {
                grid[startRow + i][col] = word[i];
            }
        }
    });

    return (
        <div>
            <p className="text-base mb-4 text-[#475569]">{content.instructions}</p>
            <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0">
                    <table className="border-collapse mx-auto" style={{ borderSpacing: 0 }}>
                        <tbody>
                            {grid.map((row, ri) => (
                                <tr key={ri}>
                                    {row.map((cell, ci) => (
                                        <td key={ci} className="w-8 h-8 text-center font-mono font-bold text-sm border border-[#e2e8f0]">
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex-1">
                    <p className="font-fredoka font-bold text-[#1e293b] mb-2">Find these words:</p>
                    <div className="grid grid-cols-2 gap-1">
                        {words.map((word, i) => (
                            <div key={i} className="flex items-start gap-2">
                                <span className="font-mono font-bold text-sm text-[#1e293b]">{word}</span>
                            </div>
                        ))}
                    </div>
                    {content.clues && (
                        <div className="mt-4">
                            <p className="font-fredoka font-bold text-[#1e293b] mb-2">Clues:</p>
                            <ol className="list-decimal pl-5 space-y-1">
                                {content.clues.map((clue, i) => (
                                    <li key={i} className="text-sm text-[#475569]">{clue}</li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            </div>
            {content.fun_fact && <FunFact text={content.fun_fact} />}
        </div>
    );
}

function FillBlanksPrint({ content }) {
    return (
        <div>
            <p className="text-base mb-4 text-[#475569]">{content.instructions}</p>
            <div className="mb-6 p-4 bg-[#f8fafc] border-2 border-dashed border-[#94a3b8] rounded-xl">
                <p className="font-fredoka font-semibold text-[#1e293b] mb-2">Word Bank</p>
                <div className="flex flex-wrap gap-3">
                    {(content.word_bank || []).map((word, i) => (
                        <span key={i} className="px-3 py-1 bg-white border-2 border-[#1e293b] rounded-lg font-semibold text-sm">{word}</span>
                    ))}
                </div>
            </div>
            <div className="space-y-4">
                {(content.paragraphs || []).map((para, i) => (
                    <p key={i} className="text-base leading-8 text-[#1e293b]">{para}</p>
                ))}
            </div>
            {content.fun_fact && <FunFact text={content.fun_fact} />}
        </div>
    );
}

function QuizTriviaPrint({ content }) {
    return (
        <div>
            <p className="text-base mb-4 text-[#475569]">{content.instructions}</p>
            <div className="space-y-5">
                {(content.questions || []).map((q, i) => (
                    <div key={i}>
                        <p className="font-semibold text-[#1e293b] mb-2">{i + 1}. {q.question}</p>
                        <div className="grid grid-cols-2 gap-2 pl-4">
                            {(q.options || []).map((opt, j) => (
                                <div key={j} className="flex items-center gap-2">
                                    <div className="w-5 h-5 border-2 border-[#1e293b] rounded-full flex-shrink-0" />
                                    <span className="text-sm text-[#475569]">{opt}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {content.bonus_question && (
                <div className="mt-6 p-4 bg-[#fef3c7] border-2 border-[#f59e0b] rounded-xl">
                    <p className="font-fredoka font-bold text-[#1e293b] mb-1">Bonus Question</p>
                    <p className="text-[#475569]">{content.bonus_question}</p>
                    <div className="mt-3 space-y-3">
                        {[1, 2, 3].map(n => <div key={n} className="border-b border-[#d1d5db] h-6" />)}
                    </div>
                </div>
            )}
            {content.fun_fact && <FunFact text={content.fun_fact} />}
        </div>
    );
}

function MatchingPrint({ content }) {
    const shuffledRight = [...(content.right_column || [])].sort(() => Math.random() - 0.5);
    return (
        <div>
            <p className="text-base mb-4 text-[#475569]">{content.instructions}</p>
            <div className="flex gap-12 justify-center">
                <div className="space-y-4">
                    {(content.left_column || []).map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-[#22c55e] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                            <span className="font-semibold text-[#1e293b] text-sm">{item}</span>
                            <div className="w-16 border-b-2 border-dashed border-[#94a3b8]" />
                        </div>
                    ))}
                </div>
                <div className="space-y-4">
                    {shuffledRight.map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-16 border-b-2 border-dashed border-[#94a3b8]" />
                            <span className="w-8 h-8 rounded-full bg-[#38bdf8] text-white font-bold text-sm flex items-center justify-center">{'ABCDEFGH'[i]}</span>
                            <span className="text-sm text-[#475569]">{item}</span>
                        </div>
                    ))}
                </div>
            </div>
            {content.fun_fact && <FunFact text={content.fun_fact} />}
        </div>
    );
}

function ColouringLabelPrint({ content }) {
    return (
        <div>
            <p className="text-base mb-4 text-[#475569]">{content.instructions}</p>
            <div className="text-center mb-6">
                <div className="inline-block border-4 border-[#1e293b] rounded-2xl p-12 bg-white" style={{ minWidth: 300, minHeight: 250 }}>
                    <p className="text-[#94a3b8] text-lg font-fredoka">{content.subject}</p>
                    <p className="text-[#94a3b8] text-sm mt-2">Draw and colour here</p>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
                {(content.labels || []).map((l, i) => (
                    <div key={i} className="p-3 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl">
                        <p className="font-bold text-sm text-[#1e293b]">{i + 1}. {l.label}</p>
                        <p className="text-xs text-[#475569]">{l.description}</p>
                        <p className="text-xs text-[#94a3b8] mt-1">Colour: {l.colour_suggestion}</p>
                    </div>
                ))}
            </div>
            {content.facts && (
                <div className="p-4 bg-[#f0fdf4] border-2 border-[#22c55e] rounded-xl">
                    <p className="font-fredoka font-bold text-[#1e293b] mb-2">Did You Know?</p>
                    <ul className="space-y-1">
                        {content.facts.map((f, i) => <li key={i} className="text-sm text-[#475569]">{f}</li>)}
                    </ul>
                </div>
            )}
            {content.fun_fact && <FunFact text={content.fun_fact} />}
        </div>
    );
}

function MissionPlannerPrint({ content }) {
    return (
        <div>
            <p className="text-base mb-4 text-[#475569]">{content.instructions}</p>
            <div className="space-y-5">
                {(content.sections || []).map((s, i) => (
                    <div key={i}>
                        <p className="font-fredoka font-bold text-[#1e293b] mb-1">{s.heading}</p>
                        <p className="text-sm text-[#475569] mb-2">{s.prompt}</p>
                        <div className="space-y-3">
                            {Array.from({ length: s.lines || 2 }).map((_, n) => (
                                <div key={n} className="border-b border-[#d1d5db] h-6" />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {content.tips && (
                <div className="mt-6 p-4 bg-[#f0fdf4] border-2 border-[#22c55e] rounded-xl">
                    <p className="font-fredoka font-bold text-[#1e293b] mb-2">Tips</p>
                    <ul className="space-y-1">
                        {content.tips.map((t, i) => <li key={i} className="text-sm text-[#475569]">{t}</li>)}
                    </ul>
                </div>
            )}
            {content.fun_fact && <FunFact text={content.fun_fact} />}
        </div>
    );
}

function FunFact({ text }) {
    return (
        <div className="mt-6 p-3 bg-[#fef3c7] border-2 border-[#f59e0b] rounded-xl text-center">
            <p className="text-sm font-semibold text-[#1e293b]">Fun Fact: {text}</p>
        </div>
    );
}

const RENDERERS = {
    word_search: WordSearchPrint,
    fill_blanks: FillBlanksPrint,
    quiz_trivia: QuizTriviaPrint,
    matching: MatchingPrint,
    colouring_label: ColouringLabelPrint,
    mission_planner: MissionPlannerPrint
};

// ==================== MAIN PAGE ====================

export default function WorksheetsPage() {
    const { user, api } = useAuth();
    const [worksheets, setWorksheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedWorksheet, setSelectedWorksheet] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterTopic, setFilterTopic] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [showGenerate, setShowGenerate] = useState(false);
    const [genType, setGenType] = useState('word_search');
    const [genTopic, setGenTopic] = useState('');
    const [genYear, setGenYear] = useState('3-4');
    const [generating, setGenerating] = useState(false);
    const printRef = useRef();

    useEffect(() => {
        loadWorksheets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, filterTopic, filterYear]);

    const loadWorksheets = async () => {
        try {
            const params = new URLSearchParams();
            if (filterType) params.set('worksheet_type', filterType);
            if (filterTopic) params.set('topic', filterTopic);
            if (filterYear) params.set('year_level', filterYear);
            const res = await api().get(`/worksheets?${params.toString()}`);
            setWorksheets(res.data);
        } catch (err) {
            console.error('Failed to load worksheets');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        if (!genTopic.trim()) { toast.error('Please enter a topic'); return; }
        setGenerating(true);
        try {
            const res = await api().post('/worksheets/generate', {
                worksheet_type: genType,
                topic: genTopic,
                year_level: genYear
            });
            toast.success('Worksheet generated!');
            setWorksheets(prev => [res.data, ...prev]);
            setShowGenerate(false);
            setGenTopic('');
            setSelectedWorksheet(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate worksheet');
        } finally {
            setGenerating(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // ==================== PRINT VIEW ====================
    if (selectedWorksheet) {
        const Renderer = RENDERERS[selectedWorksheet.worksheet_type] || (() => <p>Unknown type</p>);
        const typeInfo = WORKSHEET_TYPES[selectedWorksheet.worksheet_type] || {};

        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)]">
                {/* Screen-only controls */}
                <div className="print:hidden sticky top-0 z-50 bg-white border-b-4 border-[#1e293b] px-4 py-3">
                    <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <button
                            onClick={() => setSelectedWorksheet(null)}
                            className="flex items-center gap-2 text-[#475569] hover:text-[#1e293b] font-semibold"
                            data-testid="back-to-library"
                        >
                            <ArrowLeft weight="bold" size={20} /> Back to Library
                        </button>
                        <motion.button
                            onClick={handlePrint}
                            whileHover={{ y: -2, x: -2 }}
                            whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn bg-[#22c55e] text-white flex items-center gap-2"
                            data-testid="print-btn"
                        >
                            <Printer weight="bold" size={20} /> Print Worksheet
                        </motion.button>
                    </div>
                </div>

                {/* Printable content */}
                <div ref={printRef} className="max-w-4xl mx-auto px-4 py-8 print:px-0 print:py-0 print:max-w-none">
                    <div className="bg-white border-4 border-[#1e293b] rounded-2xl p-8 print:border-2 print:rounded-none print:shadow-none">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-[#e2e8f0]">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: typeInfo.color }}>
                                        {typeInfo.label}
                                    </span>
                                    <span className="text-xs text-[#94a3b8]">Years {selectedWorksheet.year_level}</span>
                                </div>
                                <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">{selectedWorksheet.title}</h1>
                                <p className="text-sm text-[#94a3b8] mt-1">{selectedWorksheet.topic}</p>
                            </div>
                            <div className="text-right print:block hidden">
                                <p className="font-fredoka font-bold text-[#22c55e] text-lg">Bush Rangers</p>
                            </div>
                        </div>

                        {/* Name / Date fields for print */}
                        <div className="flex gap-8 mb-6">
                            <div className="flex-1">
                                <span className="text-sm font-semibold text-[#475569]">Name: </span>
                                <span className="border-b border-[#94a3b8] inline-block" style={{ width: '80%' }}>&nbsp;</span>
                            </div>
                            <div>
                                <span className="text-sm font-semibold text-[#475569]">Date: </span>
                                <span className="border-b border-[#94a3b8] inline-block" style={{ width: 120 }}>&nbsp;</span>
                            </div>
                        </div>

                        {/* Rendered worksheet content */}
                        <Renderer content={selectedWorksheet.content} />
                    </div>
                </div>
            </div>
        );
    }

    // ==================== LIBRARY VIEW ====================
    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <div className="flex items-center gap-2">
                            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
                                <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                    <Leaf weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                    Worksheets
                                </span>
                            </Link>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link to="/dashboard" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5" data-testid="back-dashboard">
                                <ArrowLeft weight="bold" size={16} /> Dashboard
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            <div className="max-w-7xl mx-auto px-4 pt-24 pb-24">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b]">Printable Worksheets</h1>
                        <p className="text-[#475569] mt-1">Browse, generate, and print educational worksheets</p>
                    </div>
                    <motion.button
                        onClick={() => setShowGenerate(true)}
                        whileHover={{ y: -2, x: -2 }}
                        whileTap={{ y: 2, x: 2 }}
                        className="brutalist-btn bg-[#22c55e] text-white flex items-center gap-2"
                        data-testid="generate-btn"
                    >
                        <Sparkle weight="bold" size={20} /> Generate New
                    </motion.button>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <select
                        value={filterType} onChange={(e) => setFilterType(e.target.value)}
                        className="brutalist-input text-sm py-2"
                        data-testid="filter-type"
                    >
                        <option value="">All Types</option>
                        {Object.entries(WORKSHEET_TYPES).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                        ))}
                    </select>
                    <select
                        value={filterTopic} onChange={(e) => setFilterTopic(e.target.value)}
                        className="brutalist-input text-sm py-2"
                        data-testid="filter-topic"
                    >
                        <option value="">All Topics</option>
                        {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                        value={filterYear} onChange={(e) => setFilterYear(e.target.value)}
                        className="brutalist-input text-sm py-2"
                        data-testid="filter-year"
                    >
                        <option value="">All Years</option>
                        {YEAR_LEVELS.map(y => <option key={y} value={y}>Years {y}</option>)}
                    </select>
                    {(filterType || filterTopic || filterYear) && (
                        <button
                            onClick={() => { setFilterType(''); setFilterTopic(''); setFilterYear(''); }}
                            className="text-sm text-[#475569] hover:text-[#ef4444] font-semibold flex items-center gap-1"
                            data-testid="clear-filters"
                        >
                            <X size={16} /> Clear
                        </button>
                    )}
                </div>

                {/* Worksheet Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : worksheets.length === 0 ? (
                    <div className="brutalist-card p-12 text-center">
                        <GraduationCap weight="duotone" size={56} className="text-[#94a3b8] mx-auto mb-3" />
                        <p className="text-[#475569] mb-2">No worksheets found</p>
                        <p className="text-sm text-[#94a3b8]">Try adjusting filters or generate a new one</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {worksheets.map(w => {
                            const typeInfo = WORKSHEET_TYPES[w.worksheet_type] || {};
                            const Icon = typeInfo.icon || PuzzlePiece;
                            return (
                                <motion.div
                                    key={w.id}
                                    whileHover={{ y: -4, x: -4 }}
                                    className="brutalist-card p-5 cursor-pointer hover:shadow-[8px_8px_0px_0px_#1e293b] transition-shadow"
                                    onClick={() => setSelectedWorksheet(w)}
                                    data-testid={`worksheet-card-${w.id}`}
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${typeInfo.color}20` }}>
                                            <Icon weight="duotone" size={22} style={{ color: typeInfo.color }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-fredoka font-bold text-[#1e293b] text-base leading-tight">{w.title}</h3>
                                            <p className="text-xs text-[#94a3b8] mt-1">{w.topic}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: typeInfo.color }}>
                                                {typeInfo.label}
                                            </span>
                                            <span className="text-xs text-[#94a3b8]">Years {w.year_level}</span>
                                        </div>
                                        <CaretRight weight="bold" size={18} className="text-[#94a3b8]" />
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Generate Modal */}
            <AnimatePresence>
                {showGenerate && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="brutalist-card p-6 max-w-md w-full bg-white"
                        >
                            <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">Generate Worksheet</h3>
                            <p className="text-sm text-[#475569] mb-4">AI will create a printable worksheet based on your choices.</p>

                            <form onSubmit={handleGenerate} className="space-y-4">
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Type</label>
                                    <select value={genType} onChange={(e) => setGenType(e.target.value)}
                                        className="brutalist-input w-full" data-testid="gen-type">
                                        {Object.entries(WORKSHEET_TYPES).map(([k, v]) => (
                                            <option key={k} value={k}>{v.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Topic</label>
                                    <input type="text" value={genTopic} onChange={(e) => setGenTopic(e.target.value)}
                                        className="brutalist-input w-full" placeholder="e.g. Australian Frogs, Coral Reefs..."
                                        data-testid="gen-topic" />
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {TOPICS.map(t => (
                                            <button key={t} type="button" onClick={() => setGenTopic(t)}
                                                className="px-2 py-0.5 text-xs bg-[#f1f5f9] text-[#475569] rounded-full hover:bg-[#22c55e]/10 hover:text-[#22c55e] font-semibold">
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Year Level</label>
                                    <select value={genYear} onChange={(e) => setGenYear(e.target.value)}
                                        className="brutalist-input w-full" data-testid="gen-year">
                                        {YEAR_LEVELS.map(y => <option key={y} value={y}>Years {y}</option>)}
                                    </select>
                                </div>
                                <div className="flex gap-3">
                                    <button type="button" onClick={() => setShowGenerate(false)}
                                        className="brutalist-btn flex-1 bg-white text-[#1e293b]" data-testid="cancel-generate">Cancel</button>
                                    <button type="submit" disabled={generating}
                                        className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50 flex items-center justify-center gap-2"
                                        data-testid="submit-generate">
                                        {generating ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <><Sparkle weight="bold" size={18} /> Generate</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
